import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getAllComoAgents,
  getSingleComoAgent,
  createComoAgent,
  updateComoAgent,
  deleteComoAgent,
  getComoTools,
  getAccessibleComoAgents,
  getComoConversations,
  getSingleComoConversation,
  createEmptyComoConversation,
  generateComoMessage,
  updateComoConversation,
  deleteComoConversation,
  getComoSteps,
  getAccessibleEntities,
  getAllMcpConnections,
  createMcpConnection,
  updateMcpConnection,
  deleteMcpConnection,
  testMcpConnection,
  refreshMcpTools,
  uploadConversationDocument,
  getConversationDocuments,
  deleteConversationDocument,
  getLibraryDocuments,
  getLibraryQuota,
  transcribeAudio,
} from "../Api/comoAgent";
import {
  ComoAgent,
  ComoAgentCreatePayload,
  ComoTool,
  ComoConversation,
  ComoConversationDetail,
  ComoGenerateResponse,
  ComoGenerateRequest,
  ComoMessage,
  ComoEntity,
  McpConnection,
  McpConnectionPayload,
  AgentConversationDocument,
  LibraryResponse,
  LibraryQuota,
  LibraryKind,
} from "../Models/comoAgent";

// Admin hooks
export const useComoAgents = () => {
  return useQuery<ComoAgent[], Error>({
    queryKey: ["comoAgents"],
    queryFn: () => getAllComoAgents(),
  });
};

export const useSingleComoAgent = (agentId: number) => {
  return useQuery<ComoAgent, Error>({
    queryKey: ["comoAgent", agentId],
    queryFn: () => getSingleComoAgent(agentId),
    enabled: !!agentId,
  });
};

export const useCreateComoAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ComoAgentCreatePayload) => createComoAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comoAgents"] });
    },
  });
};

export const useUpdateComoAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { agentId: number; data: ComoAgentCreatePayload }) =>
      updateComoAgent(variables.agentId, variables.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["comoAgents"] });
      queryClient.invalidateQueries({
        queryKey: ["comoAgent", variables.agentId],
      });
    },
  });
};

export const useDeleteComoAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: number) => deleteComoAgent(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comoAgents"] });
    },
  });
};

export const useComoTools = () => {
  return useQuery<ComoTool[], Error>({
    queryKey: ["comoTools"],
    queryFn: async () => {
      const response = await getComoTools();
      return response.tools;
    },
    staleTime: Infinity,
  });
};

// User hooks
export const useAccessibleComoAgents = () => {
  return useQuery<ComoAgent[], Error>({
    queryKey: ["accessibleComoAgents"],
    queryFn: () => getAccessibleComoAgents(),
  });
};

export const useComoConversations = () => {
  return useInfiniteQuery({
    queryKey: ["comoConversations"],
    queryFn: ({ pageParam }) => getComoConversations(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.page_size < lastPage.total
        ? lastPage.page + 1
        : undefined,
    select: (data) => data.pages.flatMap((p) => p.conversations),
  });
};

export const useSingleComoConversation = (conversationId: number) => {
  return useQuery<ComoConversationDetail, Error>({
    queryKey: ["comoConversation", conversationId],
    queryFn: () => getSingleComoConversation(conversationId),
    enabled: !!conversationId,
    // Poll while a run is in progress server-side (a reloaded page, a dropped
    // request or a scheduled run), so its reply shows up without a refresh.
    refetchInterval: (query) =>
      query.state.data?.is_generating ? 3000 : false,
  });
};

export const useCreateEmptyComoConversation = () => {
  const queryClient = useQueryClient();
  return useMutation<ComoConversation, Error, number>({
    mutationFn: (agentId) => createEmptyComoConversation(agentId),
    onSuccess: (conversation) => {
      queryClient.setQueryData<ComoConversationDetail>(
        ["comoConversation", conversation.id],
        {
          ...conversation,
          messages: [],
          has_more_messages: false,
          is_generating: false,
          active_run_id: null,
        },
      );
      queryClient.invalidateQueries({ queryKey: ["comoConversations"] });
    },
  });
};

export const useGenerateComoMessage = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ComoGenerateResponse,
    Error,
    {
      conversationId: number;
      data: ComoGenerateRequest;
      optimisticMessage: ComoMessage;
    }
  >({
    mutationFn: (variables) =>
      generateComoMessage(variables.conversationId, variables.data),
    // The cached conversation must already hold the sent message and the
    // running state, or coming back to it within staleTime shows neither.
    onMutate: (variables) => {
      queryClient.setQueryData<ComoConversationDetail>(
        ["comoConversation", variables.conversationId],
        (old) =>
          old && {
            ...old,
            messages: [...old.messages, variables.optimisticMessage],
            is_generating: true,
            active_run_id: variables.data.run_id ?? null,
          },
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["comoConversation", variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ["comoConversations"] });
      queryClient.invalidateQueries({ queryKey: ["comoLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["comoLibraryQuota"] });
    },
    // The run may still be going server-side (e.g. a proxy timeout).
    onError: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["comoConversation", variables.conversationId],
      });
    },
  });
};

export const useDeleteComoConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: number) =>
      deleteComoConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comoConversations"] });
      queryClient.invalidateQueries({ queryKey: ["comoLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["comoLibraryQuota"] });
    },
  });
};

export const useUpdateComoConversation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ComoConversation,
    Error,
    { conversationId: number; title: string }
  >({
    mutationFn: (variables) =>
      updateComoConversation(variables.conversationId, {
        title: variables.title,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["comoConversations"] });
      queryClient.invalidateQueries({
        queryKey: ["comoConversation", variables.conversationId],
      });
    },
  });
};

export const useSetConversationPin = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ComoConversation,
    Error,
    { conversationId: number; isPinned: boolean }
  >({
    mutationFn: (variables) =>
      updateComoConversation(variables.conversationId, {
        is_pinned: variables.isPinned,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comoConversations"] });
    },
  });
};

export const useComoSteps = (runId: string | null, enabled: boolean) => {
  return useQuery<string[], Error>({
    queryKey: ["comoSteps", runId],
    queryFn: () => getComoSteps(runId as string),
    enabled: enabled && !!runId,
    refetchInterval: enabled ? 1000 : false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

export const useAccessibleEntities = () => {
  return useQuery<ComoEntity[], Error>({
    queryKey: ["accessibleEntities"],
    queryFn: () => getAccessibleEntities(),
  });
};

// Conversation document hooks
export const useConversationDocuments = (conversationId: number) => {
  return useQuery<AgentConversationDocument[], Error>({
    queryKey: ["comoConversationDocuments", conversationId],
    queryFn: () => getConversationDocuments(conversationId),
    enabled: !!conversationId,
  });
};

export const useUploadConversationDocument = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AgentConversationDocument,
    Error,
    { conversationId: number; file: File }
  >({
    mutationFn: (variables) =>
      uploadConversationDocument(variables.conversationId, variables.file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["comoConversationDocuments", variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ["comoLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["comoLibraryQuota"] });
    },
  });
};

export const useDeleteConversationDocument = () => {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { conversationId: number; documentId: number }
  >({
    mutationFn: (variables) =>
      deleteConversationDocument(variables.conversationId, variables.documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["comoConversationDocuments", variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ["comoLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["comoLibraryQuota"] });
    },
  });
};

// Library hook
export const useLibraryDocuments = (params: {
  kind: LibraryKind;
  search: string;
  page: number;
  pageSize: number;
}) => {
  return useQuery<LibraryResponse, Error>({
    queryKey: [
      "comoLibrary",
      params.kind,
      params.search,
      params.page,
      params.pageSize,
    ],
    queryFn: () =>
      getLibraryDocuments({
        kind: params.kind,
        search: params.search,
        page: params.page,
        page_size: params.pageSize,
      }),
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useLibraryQuota = (enabled = true) => {
  return useQuery<LibraryQuota, Error>({
    queryKey: ["comoLibraryQuota"],
    queryFn: () => getLibraryQuota(),
    staleTime: 0,
    refetchOnMount: "always",
    enabled,
  });
};

// MCP Connection hooks
export const useMcpConnections = () => {
  return useQuery<McpConnection[], Error>({
    queryKey: ["mcpConnections"],
    queryFn: () => getAllMcpConnections(),
  });
};

export const useCreateMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: McpConnectionPayload) => createMcpConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcpConnections"] });
    },
  });
};

export const useUpdateMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: number; data: McpConnectionPayload }) =>
      updateMcpConnection(variables.id, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcpConnections"] });
    },
  });
};

export const useDeleteMcpConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMcpConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcpConnections"] });
    },
  });
};

export const useTestMcpConnection = () => {
  return useMutation({
    mutationFn: (id: number) => testMcpConnection(id),
  });
};

export const useRefreshMcpTools = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => refreshMcpTools(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcpConnections"] });
    },
  });
};

export const useTranscribeAudio = () => {
  return useMutation<
    string,
    Error,
    { audio: Blob; filename: string; language?: string }
  >({
    mutationFn: (variables) =>
      transcribeAudio(variables.audio, variables.filename, variables.language),
  });
};
